import Head from "next/head";
import NextLink from "next/link";
import { useRouter } from "next/compat/router";
import { Heading, Text, Box, Link } from "@cruk/cruk-react-components";
import { checkIfDev } from "@fwa/src/utils/envUtils";

import { DrawingCanvasControls } from "@fwa/src/components/DrawingCanvasControls";

import sharedStyles from "@fwa/src/components/styles.module.css";

const image404 = "/assets/images/img/404.svg";

export const ErrorPage = ({
  statusCode,
  error,
}: {
  statusCode: number;
  error?: Error;
}) => {
  const router = useRouter();
  const isDev = checkIfDev();
  const notFound = statusCode === 404;
  const missingSlug =
    (notFound &&
      router?.asPath.slice(router.asPath.lastIndexOf("/") + 1).split("?")[0]) ||
    "";

  const isPotentialFWSDownTime =
    [408].includes(statusCode) || statusCode.toString().startsWith("5");

  const statusMessages: Record<number, string> = {
    400: "Bad Request.",
    401: "You do not have permission to see this page.",
    403: "You need to be logged in to see this page.",
    404: "We can't seem to find the page you are looking for.",
    405: "Method Not Allowed.",
    410: "This page has been removed.",
    500: "Internal Server Error.",
  };

  const additionDevInfo = `${statusCode || ""}: ${error?.name || ""}, ${
    error?.message || ""
  }, ${JSON.stringify(error?.stack)}`;

  const title =
    statusMessages[statusCode] || `An unexpected error has occurred`;

  let headingText;

  switch (statusCode) {
    case 401:
      headingText = "Access denied";
      break;
    case 403:
      headingText = "Access denied";
      break;
    case 404:
      headingText = "Page not found";
      break;
    case 410:
      headingText = "Removed";
      break;
    default:
      headingText = "Something went wrong";
  }

  return (
    <>
      <Head>
        <title>{`Error ${statusCode}: ${title}`}</title>
      </Head>
      <div className={sharedStyles["content-max-width"]}>
        <div
          className={[
            "spacing-props",
            "color-props",
            sharedStyles["content-wrapper-responsive"],
          ].join(" ")}
          data-margin-top="l"
          data-bg-color="background-light"
          data-margin-bottom="l"
        >
          <div className={sharedStyles["row"]}>
            {notFound && (
              <Box>
                <img src={image404} alt="" />
              </Box>
            )}

            <div style={{ float: "left" }}>
              <Heading h1>{headingText}</Heading>
              <Text>Error: {statusCode}</Text>
              {isDev && <Text suppressHydrationWarning>{additionDevInfo}</Text>}
              <Text>{title}</Text>
              {!!isPotentialFWSDownTime && (
                <Text>
                  {`Sorry this page isn't available right now. Please refresh the page. If that doesn't work, try again in 10 minutes or draw something below while you wait.`}
                </Text>
              )}
              {!!missingSlug.length && (
                <Text>
                  {`Please check the web address and try again or you can try `}
                  <NextLink
                    suppressHydrationWarning
                    href={`/?q=${missingSlug}`}
                    data-cta-type="link-return-page"
                  >
                    <Link as="span">searching</Link>
                  </NextLink>
                  {` for what you are looking for.`}
                </Text>
              )}
            </div>
          </div>
          {!!isPotentialFWSDownTime && (
            <Box marginTop="l">
              <DrawingCanvasControls />
            </Box>
          )}
        </div>
      </div>
    </>
  );
};

export default ErrorPage;
